import { Injectable, InjectionToken } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, noop } from 'rxjs';
import { placerOrderData } from 'estensa-module';
import { HttpClient } from '@angular/common/http';
import { ReportWorkflowService } from './report-workflow.service';
import { VisitService } from './rest/visit.service';
import { getReportingPageFromExamination } from '../custom-pages/reporting-check';
import { SearchFormType } from '../search-page/search-page-type.enum';
import { ReportingDataBuilder } from './reporting-data-builder.service';
import { HttpMasterDataResolver } from '../custom-pages/services/http-data-resolver';
import { ServerRootService } from '../utils/routing/serverroot.service';
import { RouterStackService } from '../utils/routing/router-stack.service';
import { SpecialtyReportingRestService } from './specialty-reporting-rest.service';
import { Router } from '@angular/router';
import { AuthenticationSSOService } from './angularjs/authentication-sso.service';
import { ExaminationWorklistDTO } from '../models/worklist.models';
import { ExaminationWorkflowDTO } from '../models/workflow.models';
import { VisitExamination } from '../models';

import { IReportWorkflowExamService } from './report-workflow-exam.service';
import { WorkflowService } from './rest/workflow.service';
import { WorklistService } from './rest/worklist.service';

export const ESTENSA_MODULE_THIRD_PART_SUPPORT_SERVICE = new InjectionToken(
    'estensa.module.third.part.support.service',
);

@Injectable()
export class NullThirdPartSupportService {
    initService = noop;
    destroy = noop;
    getExaminationById = noop;
    getExaminationIdFromPlacerOrderData = noop;
    goToVisit = noop;
    goToECG = noop;
}

@Injectable({
    providedIn: 'root',
})
export class ThirdPartSupportService extends ServerRootService {

    constructor(
        private reportingDataBuilder: ReportingDataBuilder,
        private httpClient: HttpClient,
        private httpMasterDataResolver: HttpMasterDataResolver,
        private reportWorkflowService: ReportWorkflowService,
        private routerStackService: RouterStackService,
        private visitService: VisitService,
        private specialtyRestService: SpecialtyReportingRestService,
        private router: Router,
        private authenticationSSO: AuthenticationSSOService,
        private workflowService: WorkflowService,
        private worklistService: WorklistService,
        private translateService: TranslateService
    ) {
        super();
    }

    public getExaminationIdFromPlacerOrderData = (placerOrderData: placerOrderData) => {
        //chiamare metodo marce
        return this.httpClient.post(
            this.getServerRoot() + '/Integration/GetExaminationIdFromPlacerOrderData',
            placerOrderData,
            this.buildOptions(),
        );
    };

    async goToReporting(busData: any) {

        let errorMessage: string = '';
        try {

            const examination: ExaminationWorklistDTO = await firstValueFrom(this.worklistService.getExamination(busData?.examinationId));
            const examinationDetails: ExaminationWorkflowDTO = await firstValueFrom(this.workflowService.getExaminationDetails(busData?.examinationId));

            // Recupero i placerOrderData per le verifiche
            /* ripristinare dopo debug altrimenti fallisce sempre */
            if (busData?.pt) {
                let data = await firstValueFrom(this.authenticationSSO.getPairingData(busData?.pt));

                const placerOrderData: placerOrderData = JSON.parse(data.parameterDictionary.placerOrderData);
                if (examination.patient.patientId !== placerOrderData.PatientId) {
                    errorMessage = "ThidPartAccess_DifferentPatientId"; // E' stato trovato un esame con un PatientId diverso da quello richiesto"
                    return;
                }

                if (examination.patient.issuer !== placerOrderData.PatientIdIssuer) {
                    errorMessage = "ThidPartAccess_DifferentPatientIssuer"; // E' stato trovato un esame con un PatientIdIssuer diverso da quello richiesto"
                    return;
                }
            }

            const check = getReportingPageFromExamination(examinationDetails);
            if (check === SearchFormType.VISIT) {
                const params = await this.reportingDataBuilder.prepareCpParams({
                    addExaminationsToReport: false,
                    examinations: [examination as any],
                    services: {
                        examService: this.visitService,
                        masterDataResolver: this.httpMasterDataResolver,
                        reportWorkflowService: this.reportWorkflowService,
                    },
                    isFromThirdPart: true
                });
                this.routerStackService.go('/new/vnext/cardio-visit/report', params);
            } else if (check === SearchFormType.ECG) {
                const params = this.reportingDataBuilder.prepareECGParamsEx([examinationDetails], true);
                this.routerStackService.go('/new/vnext/ecg/reporting-details', params);

            } else if (check === SearchFormType.SPECIALTY) {
                const params = await this.reportingDataBuilder.prepareCpParams({
                    addExaminationsToReport: false,
                    examinations: [examination as any],
                    services: {
                        examService: this.specialtyRestService as IReportWorkflowExamService,
                        masterDataResolver: this.httpMasterDataResolver,
                        reportWorkflowService: this.reportWorkflowService,
                    },
                    isFromThirdPart: true
                });

                this.routerStackService.go('/new/vnext/specialty-reporting/report', params);
            }
            else
                errorMessage = 'ThidPartAccess_UnknownReportingModule';
        }
        catch (ex) {
            console.log(ex);
            errorMessage = 'ThidPartAccess_GenericError';
        }
        finally {
            if (errorMessage != '') {
                if (errorMessage != 'ThidPartAccess_GenericError')
                    console.error(this.translateService.instant('ThidPartAccess_GenericError') + ". " +
                        this.translateService.instant(errorMessage));
                else
                    console.error(this.translateService.instant(errorMessage));

                this.router.navigate(['/login']);
            }
        }
    }

    public isThirdPartPage(): boolean {
        if (this.routerStackService.lastState?.params?.isFromThirdPart === true) {
            // Per ora l'accesso alle terze parti è fatto solo su queste pagine
            if (this.routerStackService.lastState?.to == '/new/vnext/ecg/reporting-details' ||
                this.routerStackService.lastState?.to == '/new/vnext/cardio-visit/report' ||
                this.routerStackService.lastState?.to == '/new/vnext/specialty-reporting/report')
                return true;
        }

        return false;
    }
}
